import React, { useMemo, useState } from "react";
import { View, StyleSheet } from "react-native";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import NtModalInputTitle from "../../components/NtModalInputTitle";
import NtDropDownSelectContainer from "../../components/NtDropDownSelectContainer";
import NtTabController from "../../components/NtTabController";
import NtCard from "../../components/NtCard";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtModalError from "../../components/NtModalError";
import NtText from "../../components/NtText";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtAppearIn from "../../components/NtAppearIn";
import { useTheme } from "../../context/ThemeContext";
import Logger from "../../common/utils/Logger";
import { reserveNumber, assignNumber, searchNpa, searchNpaNxx } from "../../api/number";
import { formatTelephoneNumber, extractNpas, extractNxxs } from "../utils/numberUtils";
import { COUNTRIES, regionsFor } from "../data/dialingRegions";

// Step header (numbered badge + title/subtitle) — small presentational helper.
function StepHeader({ number, title, subtitle, done }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  return (
    <View style={styles.stepHd}>
      <View style={[styles.stepNo, { backgroundColor: theme.brand2 }]}>
        {done ? (
          <NtMaterialIcon name="check" size={12} color="#fff" />
        ) : (
          <NtText style={styles.stepNoText}>{number}</NtText>
        )}
      </View>
      <View style={{ flex: 1 }}>
        <NtText style={styles.stepTitle}>{title}</NtText>
        <NtText style={styles.stepSub}>{subtitle}</NtText>
      </View>
    </View>
  );
}

/**
 * Get-a-number flow — guided steps. Step 1 Location (country + state), Step 2
 * Number (area code + prefix, live-fetched), with a preview of the forming
 * number. Both NPA and NXX are required by the reserve API. Then reserve (hold
 * a match) → assign (claim + bind to the trunk).
 */
function NtNumberNewForm({ onCancel, onCreate }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const [country, setCountry] = useState(null);
  const [region, setRegion] = useState(null);

  const [npaSel, setNpaSel] = useState(null);
  const [npaManual, setNpaManual] = useState("");
  const [npaOptions, setNpaOptions] = useState([]);
  const [npaLoading, setNpaLoading] = useState(false);
  const [npaFallback, setNpaFallback] = useState(false);

  const [nxxSel, setNxxSel] = useState(null);
  const [nxxManual, setNxxManual] = useState("");
  const [nxxOptions, setNxxOptions] = useState([]);
  const [nxxLoading, setNxxLoading] = useState(false);
  const [nxxFallback, setNxxFallback] = useState(false);

  const [reserved, setReserved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const effectiveNpa = (npaSel?.code || npaManual).replace(/\D/g, "");
  const effectiveNxx = (nxxSel?.code || nxxManual).replace(/\D/g, "");
  const npaValid = /^\d{3}$/.test(effectiveNpa);
  const nxxValid = /^\d{3}$/.test(effectiveNxx);
  const locationDone = !!country && !!region;
  const canSearch = locationDone && npaValid && nxxValid;

  const countryOptions = useMemo(
    () => COUNTRIES.map((c) => ({ id: c.code, title: c.title })),
    [],
  );

  const clearNxx = () => {
    setNxxSel(null);
    setNxxManual("");
    setNxxOptions([]);
    setNxxFallback(false);
    setNxxLoading(false);
  };

  const loadAreaCodes = async (countryCode, regionCode) => {
    setNpaLoading(true);
    setNpaFallback(false);
    setNpaOptions([]);
    try {
      const res = await searchNpa(countryCode, regionCode);
      Logger("Number search/npa", res);
      const codes = res?.ok ? extractNpas(res.data) : [];
      if (codes.length) setNpaOptions(codes.map((c) => ({ title: c, code: c })));
      else setNpaFallback(true);
    } catch (e) {
      Logger("Number search/npa failed", e);
      setNpaFallback(true);
    } finally {
      setNpaLoading(false);
    }
  };

  const loadNxx = async (countryCode, regionCode, npa) => {
    setNxxLoading(true);
    setNxxFallback(false);
    setNxxOptions([]);
    setNxxSel(null);
    setNxxManual("");
    try {
      const res = await searchNpaNxx(countryCode, regionCode, { npa });
      Logger("Number search/npanxx", res);
      const codes = res?.ok ? extractNxxs(res.data) : [];
      if (codes.length) setNxxOptions(codes.map((c) => ({ title: c, code: c })));
      else setNxxFallback(true);
    } catch (e) {
      Logger("Number search/npanxx failed", e);
      setNxxFallback(true);
    } finally {
      setNxxLoading(false);
    }
  };

  const onSelectCountry = (item) => {
    setCountry(item);
    setRegion(null);
    setNpaSel(null);
    setNpaManual("");
    setNpaOptions([]);
    setNpaFallback(false);
    clearNxx();
    setError(null);
  };

  const onSelectRegion = (item) => {
    setRegion(item);
    setNpaSel(null);
    setNpaManual("");
    clearNxx();
    setError(null);
    loadAreaCodes(country.code, item.code);
  };

  const onSelectNpa = (item) => {
    setNpaSel(item);
    setNpaManual("");
    loadNxx(country.code, region.code, item.code);
  };

  const onChangeManualNpa = (t) => {
    const v = t.replace(/\D/g, "").slice(0, 3);
    setNpaManual(v);
    clearNxx();
    if (/^\d{3}$/.test(v)) loadNxx(country.code, region.code, v);
  };

  const mapErr = (res, fallback) => {
    const detail = res?.data?.error || res?.data?.detail || res?.data?.title || "";
    if (res?.status === 409) {
      if (/provision/i.test(detail))
        return "Your VoIP isn't set up yet. Upgrade your plan to get a number.";
      return "That number is already on your account.";
    }
    if (res?.status === 403)
      return `Your account can't get numbers yet${detail ? ` (${detail})` : ""}. Please contact support.`;
    if (res?.status === 422)
      return "That area code / prefix has no available number — try another.";
    if (res?.status === 502)
      return "The number service is temporarily unavailable. Please try again.";
    return detail || fallback;
  };

  const handleReserve = async () => {
    if (!canSearch) return;
    setLoading(true);
    setError(null);
    const res = await reserveNumber({
      country: country.code,
      region: region.code,
      npa: effectiveNpa,
      nxx: effectiveNxx,
    });
    Logger("Number reserve", res);
    setLoading(false);
    if (res.ok && res.data?.tn) setReserved({ tn: res.data.tn });
    else setError(mapErr(res, "Couldn't find a number. Try a different area code."));
  };

  const handleAssign = async () => {
    if (!reserved?.tn) return;
    setLoading(true);
    setError(null);
    const res = await assignNumber(reserved.tn);
    Logger("Number assign", res);
    setLoading(false);
    if (res.ok) onCreate?.(formatTelephoneNumber(reserved.tn));
    else setError(mapErr(res, "Couldn't assign that number. Please try again."));
  };

  // ---------- Reserved: confirm to claim ----------
  if (reserved) {
    return (
      <View style={styles.container}>
        <NtModalTitle>Confirm your number</NtModalTitle>
        <NtModalSubtitle style={{ marginTop: 4 }}>
          We're holding this number for you. Claim it to add it to your account.
        </NtModalSubtitle>

        <NtAppearIn>
          <NtCard style={[styles.numberCard, { borderColor: theme.brand2 }]}>
            <View style={{ alignItems: "center" }}>
              <NtText style={styles.number}>{formatTelephoneNumber(reserved.tn)}</NtText>
              <NtText style={styles.hint}>Reserved · held briefly</NtText>
            </View>
          </NtCard>
        </NtAppearIn>

        <NtModalError error={error} containerStyle={{ marginTop: 6 }} />

        <View style={styles.footer}>
          {loading && <NtActivityIndicator color={theme.brand2} size="small" containerStyle={{ marginRight: 20 }} />}
          <NtModalUnactiveButton title="Find another" onPress={() => { setReserved(null); setError(null); }} />
          <NtModalActiveButton containerStyle={{ marginLeft: 15 }} title="Get this number" onPress={handleAssign} enabled={!loading} />
        </View>
      </View>
    );
  }

  // ---------- Search ----------
  // Returns just the control (no label) at a uniform 38px height so the two
  // columns line up in every state (dropdown / typed / loading).
  const control = (cfg) => {
    if (cfg.loading) {
      return (
        <View style={styles.loadingField}>
          <NtActivityIndicator color={theme.brand2} size="small" />
          <NtText style={styles.loadingText}>Loading…</NtText>
        </View>
      );
    }
    if (cfg.fallback) {
      // Free-text entry — a numeric example (e.g. "561") reads as a format hint.
      return (
        <NtModalInputField
          containerStyle={{ marginBottom: 0 }}
          placeholder={cfg.placeholder}
          keyboardType="number-pad"
          value={cfg.manualValue}
          setValue={cfg.onChangeManual}
        />
      );
    }
    // Dropdown — use a "Select…" prompt so an empty field never looks pre-filled.
    return (
      <NtDropDownSelectContainer
        placeholder={cfg.selectPlaceholder || cfg.placeholder}
        data={cfg.options}
        selection={cfg.selection}
        onSelect={cfg.onSelectItem}
      />
    );
  };

  const npaText = npaValid ? effectiveNpa : "•••";
  const nxxText = nxxValid ? effectiveNxx : "•••";

  return (
    <View style={styles.container}>
      <NtModalTitle>Get a number</NtModalTitle>
      <NtModalSubtitle style={{ marginTop: 4 }}>
        Two quick steps and we'll find you a number.
      </NtModalSubtitle>

      {/* Step 1 — Location */}
      <NtAppearIn style={{ zIndex: 30, marginTop: 16 }}>
        <NtCard style={styles.stepCard}>
          <StepHeader number="1" title="Location" subtitle="Where should the number be based?" done={locationDone} />

          <NtTabController
            options={countryOptions}
            selection={countryOptions.find((o) => o.id === country?.code)}
            onPress={(item) => onSelectCountry(COUNTRIES.find((c) => c.code === item.id))}
          />

          {country ? (
            <NtAppearIn style={{ marginTop: 14 }}>
              <NtModalInputTitle style={styles.fieldLabel}>State / Province</NtModalInputTitle>
              <NtDropDownSelectContainer
                placeholder="Select a state or province"
                data={regionsFor(country.code)}
                selection={region}
                onSelect={onSelectRegion}
              />
            </NtAppearIn>
          ) : null}
        </NtCard>
      </NtAppearIn>

      {/* Step 2 — Number */}
      {locationDone ? (
        <NtAppearIn style={{ zIndex: 20, marginTop: 14 }}>
          <NtCard style={styles.stepCard}>
            <StepHeader number="2" title="Number" subtitle="Area code &amp; prefix" />

            <View style={styles.twoCol}>
              <View style={{ flex: 1, zIndex: 2 }}>
                <NtModalInputTitle style={styles.fieldLabel}>Area code</NtModalInputTitle>
                {control({
                  loading: npaLoading,
                  fallback: npaFallback,
                  options: npaOptions,
                  selection: npaSel,
                  onSelectItem: onSelectNpa,
                  manualValue: npaManual,
                  onChangeManual: onChangeManualNpa,
                  selectPlaceholder: "Select area code",
                  placeholder: "561",
                })}
              </View>
              <View style={{ flex: 1, zIndex: 1 }}>
                <NtModalInputTitle style={styles.fieldLabel}>Prefix</NtModalInputTitle>
                {npaValid ? (
                  control({
                    loading: nxxLoading,
                    fallback: nxxFallback,
                    options: nxxOptions,
                    selection: nxxSel,
                    onSelectItem: (item) => { setNxxSel(item); setNxxManual(""); },
                    manualValue: nxxManual,
                    onChangeManual: (t) => { setNxxManual(t.replace(/\D/g, "").slice(0, 3)); setNxxSel(null); },
                    selectPlaceholder: "Select prefix",
                    placeholder: "289",
                  })
                ) : (
                  <View style={styles.disabledField}>
                    <NtText style={styles.disabledText}>Pick an area code first</NtText>
                  </View>
                )}
              </View>
            </View>
          </NtCard>
        </NtAppearIn>
      ) : null}

      {/* Live preview */}
      {locationDone ? (
        <NtAppearIn style={{ marginTop: 14 }}>
          <View style={styles.preview}>
            <NtText style={styles.previewText}>
              +1 (<NtText style={styles.previewStrong}>{npaText}</NtText>){" "}
              <NtText style={styles.previewStrong}>{nxxText}</NtText>
              <NtText style={styles.previewTail}> – ● ● ● ●</NtText>
            </NtText>
          </View>
        </NtAppearIn>
      ) : null}

      <NtModalError error={error} containerStyle={{ marginTop: 10 }} />

      <View style={styles.footer}>
        {loading && <NtActivityIndicator color={theme.brand2} size="small" containerStyle={{ marginRight: 20 }} />}
        <NtModalUnactiveButton title="Cancel" onPress={onCancel} />
        <NtModalActiveButton
          containerStyle={{ marginLeft: 15 }}
          title="Find my number"
          icon="magnify"
          onPress={handleReserve}
          enabled={canSearch && !loading}
        />
      </View>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    container: { flex: 1, minWidth: 500, maxWidth: 600, padding: 15 },

    stepCard: { padding: 14, marginLeft: 0, marginRight: 0 },
    stepHd: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 12 },
    stepNo: { width: 20, height: 20, borderRadius: 10, alignItems: "center", justifyContent: "center" },
    stepNoText: { color: "#fff", fontSize: 11, fontWeight: "800" },
    stepTitle: { color: t.text, fontSize: 13, fontWeight: "700" },
    stepSub: { color: t.textMute, fontSize: 10.5, marginTop: 1 },

    fieldLabel: { marginBottom: 5, marginLeft: 4 },

    twoCol: { flexDirection: "row", gap: 10, marginTop: 14 },
    disabledField: {
      height: 38,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
      backgroundColor: t.surface,
      paddingHorizontal: 11,
      justifyContent: "center",
      opacity: 0.7,
    },
    disabledText: { color: t.textMute, fontSize: 12 },
    loadingField: {
      height: 38,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
      backgroundColor: t.surface2,
      paddingHorizontal: 11,
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    loadingText: { fontSize: 12.5, color: t.textDim },

    preview: { backgroundColor: t.surface3, borderRadius: 8, paddingVertical: 12, alignItems: "center" },
    previewText: { fontFamily: t.fontMono || "Menlo", fontSize: 20, color: t.textDim, letterSpacing: 1 },
    previewStrong: { color: t.text, fontWeight: "700" },
    previewTail: { color: t.textMute },

    numberCard: { marginTop: 22, padding: 20, marginLeft: 0, marginRight: 0, borderWidth: 1 },
    number: { fontFamily: t.fontMono || "Menlo", fontSize: 26, fontWeight: "800", letterSpacing: -0.5, color: t.text },
    hint: { fontSize: 11, marginTop: 6, color: t.textMute },

    footer: { flexDirection: "row", alignItems: "center", justifyContent: "flex-end", marginTop: 22 },
  });

export default NtNumberNewForm;
